<template>
  <div class="blood-pressure-taken-check">
    <div class="el-input-group el-input-group--append el-input-group--prepend">
      <div v-if="warnings.length" class="el-input-group__prepend">
        <help-tooltip type="warning">
          <span v-for="warning in warnings" :key="warning.message">
            {{ warning.message }}
            <br>
          </span>
        </help-tooltip>
      </div>
      <el-select
        v-close-on-zoom
        class="is-current-select"
        :value="state.check.isCurrent"
        :disabled="readonly"
        @input="isCurrent => updateCurrent(isCurrent)"
      >
        <el-option :value="false" :label="'Was Blood Pressure taken'" />
        <el-option :value="true" :label="'Is Blood Pressure being taken'" />
      </el-select>
    </div>
    <div v-if="!state.check.isCurrent" class="el-input-group el-input-group--append el-input-group--prepend">
      <el-select
        v-close-on-zoom
        :value="state.check.mode"
        class="mode-select"
        :disabled="readonly"
        @input="mode => updateMode(mode)"
      >
        <el-option v-for="mode in modes" :key="mode.value" :label="mode.label" :value="mode.value" />
      </el-select>
    </div>
    <div v-if="state.check.isCurrent || state.check.mode === 'AT_INTERVAL'">
      <div class="el-input-group el-input-group--append el-input-group--prepend">
        <el-select
          v-close-on-zoom
          :value="state.check.intervals"
          :disabled="readonly"
          multiple
          placeholder="with any interval"
          class="interval-select"
          @input="intervals => updateIntervals(intervals)"
        >
          <el-option
            v-for="interval in bpIntervals"
            :key="interval.key"
            :value="interval.key"
            :label="interval.label.en"
          />
        </el-select>
      </div>
    </div>

    <div v-if="!state.check.isCurrent && state.check.mode !== 'AT_INTERVAL'" class="el-input-group el-input-group--append el-input-group--prepend">      
      <el-select
        id="operator-select"
        v-close-on-zoom
        :value="state.check.operator"
        placeholder="exactly"
        class="operator-select"
        :disabled="readonly"
        @input="operator => updateOperator(operator)"
      >
        <el-option v-for="op in operators" :key="op.value" :label="op.label" :value="op.value" />
      </el-select>
      <el-input
        v-integer-only
        type="number"
        placeholder="any amount of"
        :disabled="readonly"
        :value="state.check.count"
        :min="0"
        class="count-input"
        @change="value => updateCount(parseInt(value))"
      >
        <template slot="append">times</template>
      </el-input>
    </div>
  </div>
</template>

<script>
import { get, isNumber, uniqBy } from 'lodash';

export default {
  name: 'blood-pressure-taken-check',
  components: {},
  props: ['readonly', 'emitter', 'ikey', 'getData', 'putData', 'control'],
  data() {
    return {
      state: {
        check: {
          intervals: [],
          isCurrent: true,
          count: null,
          operator: 'EQUAL',
          mode: 'SPECIFIC_COUNT',
        },
      },
      modes: [
        { value: 'MANUALLY', label: 'manually' },
        { value: 'AT_INTERVAL', label: 'at interval' },
        { value: 'SPECIFIC_COUNT', label: 'with specific count' },
      ],
      operators: [
        { value: 'LESS_THAN', label: 'less than' },
        { value: 'EQUAL', label: 'exactly' },
        { value: 'GREATER_THAN', label: 'more than' },
      ],
      bpIntervals: [
        {
          key: 'EVERY_1_MINUTE',
          label: {
            en: 'Every 1 minute',
          },
        },
        {
          key: 'EVERY_3_MINUTES',
          label: {
            en: 'Every 3 minutes',
          },
        },
        {
          key: 'EVERY_5_MINUTES',
          label: {
            en: 'Every 5 minutes',
          },
        },
        {
          key: 'EVERY_10_MINUTES',
          label: {
            en: 'Every 10 minutes',
          },
        },
        {
          key: 'EVERY_15_MINUTES',
          label: {
            en: 'Every 15 minutes',
          },
        },
      ],
    };
  },
  computed: {
    warnings() {
      if (!this.emitter.$store) {
        return [];
      }
      const warnings = get(
        this.emitter.$store.getters['validation/visibleWarningsByFieldAndId'],
        `caseLogicDescription.${this.control.parent.id}`,
        [],
      ).filter(warning => warning.metadata && warning.metadata.checkKey === this.ikey);

      return uniqBy(warnings, 'message');
    },
  },
  mounted() {
    this.state.check = { ...this.state.check, ...this.getData(this.ikey) };
    this.emitter.trigger('process', this.control);
    this.update();
  },
  methods: {
    updateIntervals(intervals) {
      this.state.check.intervals = intervals;
      this.update();
    },
    updateCurrent(isCurrent) {
      this.state.check.isCurrent = isCurrent;
      this.state.check.intervals = [];
      this.update();
    },
    updateCount(count) {
      this.state.check.count = isNumber(count) ? count : null;
      this.update();
    },
    updateMode(mode) {
      this.state.check.mode = mode;
      this.state.check.intervals = []; // override old data that had 'ONCE'
      this.update();
    },
    updateOperator(operator) {
      this.state.check.operator = operator;
      this.update();
    },
    update() {
      this.putData(this.ikey, {
        type: 'VITAL_ASSESS',
        id: 'BLOOD_PRESSURE_BOTH',
        value: true,
        ...this.state.check,
      });
      this.emitter.trigger('process', this.control);
    },
  },
};
</script>

<style scoped>
.blood-pressure-taken-check {
  display: flex;
  align-items: flex-start;
}
.blood-pressure-taken-check div {
  width: auto;
}

.is-current-select {
  width: 235px !important;
}
</style>
